import { useState } from 'react';
import { Box, Chip, Collapse, Link, Skeleton, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { EventSubscribeBell } from '../../../../notifications/EventSubscribeBell';
import { SubscribeButton } from '../../../../notifications/SubscribeButton';
import { MonitorLinkChips } from '../../../monitor/links/MonitorLinkChips';
import { formatDaysRemaining, formatVolume } from '../../utils/format';
import type { EventModel } from '../model/types';
import {
    PageHeader,
    PageMeta,
} from '../../../../components/common/page/PageHeader';

interface EventHeaderProps {
    event: EventModel | null;
    loading: boolean;
    /** null while the alerts are still loading. */
    alertCount: number | null;
}

function noop() {}

// A placeholder around a stand-in takes the stand-in's own height: as a flex
// box its child is laid out as it would be in the header, not as a line of
// the placeholder's (larger) text.
const FIT = { display: 'flex' } as const;

// Stand-ins of a typical length, drawn invisible under the placeholders so the
// header wraps the way a real one does (on a phone the title and the details
// take several lines) and keeps its height when the event arrives.
const TITLE_STAND_IN = 'Loading the title of this event';
const META_STAND_INS = [
    'Nov 8, 2028 · ~24 months left',
    '53 outcomes',
    '$1298.1M vol',
    '0 alerts',
    'Open market',
    'Resolution details',
];

function HeaderPlaceholder() {
    return (
        <Box data-testid="event-header-placeholder" aria-busy="true">
            <PageHeader
                leading={
                    <Skeleton
                        variant="rounded"
                        width={40}
                        height={40}
                        sx={{ flexShrink: 0 }}
                    />
                }
                title={
                    <Skeleton>
                        <span>{TITLE_STAND_IN}</span>
                    </Skeleton>
                }
                meta={
                    <>
                        <Skeleton variant="rounded" sx={FIT}>
                            <Chip
                                size="small"
                                label="Open"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '11px' }}
                            />
                        </Skeleton>
                        {META_STAND_INS.map(text => (
                            <Skeleton key={text} sx={FIT}>
                                <PageMeta>{text}</PageMeta>
                            </Skeleton>
                        ))}
                    </>
                }
            >
                <Skeleton variant="rounded" sx={FIT}>
                    <SubscribeButton
                        subscribed={false}
                        onClick={noop}
                        disabled
                    />
                </Skeleton>
            </PageHeader>
        </Box>
    );
}

export function EventHeader({ event, loading, alertCount }: EventHeaderProps) {
    const [descOpen, setDescOpen] = useState(false);

    if (!event) {
        return loading ? <HeaderPlaceholder /> : null;
    }

    const daysLeft =
        event.status === 'open' && event.endDate
            ? formatDaysRemaining(event.endDate.toISOString())
            : null;
    const outcomeCount = event.outcomes.length;

    return (
        <Box data-testid="event-header">
            <PageHeader
                leading={
                    event.image && (
                        <Box
                            component="img"
                            src={event.image}
                            alt=""
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1,
                                objectFit: 'cover',
                                flexShrink: 0,
                            }}
                        />
                    )
                }
                title={event.title}
                meta={
                    <>
                        <Chip
                            size="small"
                            label={event.status === 'open' ? 'Open' : 'Closed'}
                            color={
                                event.status === 'open' ? 'success' : 'default'
                            }
                            variant="outlined"
                            sx={{ height: 20, fontSize: '11px' }}
                        />
                        <MonitorLinkChips
                            eventId={event.id}
                            slug={event.slug}
                        />
                        {event.endDate && (
                            <PageMeta>
                                {event.endDate.toLocaleDateString(undefined, {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                })}
                                {daysLeft ? ` · ${daysLeft}` : ''}
                            </PageMeta>
                        )}
                        <PageMeta>
                            {outcomeCount} outcome
                            {outcomeCount !== 1 ? 's' : ''}
                        </PageMeta>
                        <PageMeta>
                            {formatVolume(event.totalVolume)} vol
                        </PageMeta>
                        {alertCount === null ? (
                            <Skeleton
                                data-testid="alert-count-placeholder"
                                sx={FIT}
                            >
                                <PageMeta>0 alerts</PageMeta>
                            </Skeleton>
                        ) : (
                            <PageMeta>
                                {alertCount} alert{alertCount !== 1 ? 's' : ''}
                            </PageMeta>
                        )}
                        {event.sources.map(source =>
                            source.id === 'polymarket' && !event.slug ? null : (
                                <PageMeta key={source.id}>
                                    {source.id === 'polymarket' &&
                                    event.slug ? (
                                        <Link
                                            href={`https://polymarket.com/event/${event.slug}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="hover"
                                            color="inherit"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.25,
                                            }}
                                        >
                                            Open market
                                            <OpenInNewIcon
                                                sx={{ fontSize: 12 }}
                                            />
                                        </Link>
                                    ) : (
                                        source.label
                                    )}
                                </PageMeta>
                            )
                        )}
                        {event.description && (
                            <Typography
                                component="span"
                                onClick={() => setDescOpen(v => !v)}
                                sx={{
                                    fontSize: '12px',
                                    color: 'text.secondary',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    '&:hover': { color: 'text.primary' },
                                }}
                            >
                                {descOpen
                                    ? 'Hide details'
                                    : 'Resolution details'}
                            </Typography>
                        )}
                    </>
                }
            >
                <EventSubscribeBell
                    eventId={event.id}
                    eventTitle={event.title || 'Event'}
                />
            </PageHeader>
            {event.description && (
                <Collapse in={descOpen}>
                    <Typography
                        sx={{
                            mt: 1,
                            fontSize: '12px',
                            color: 'text.secondary',
                            whiteSpace: 'pre-wrap',
                            maxWidth: 900,
                        }}
                    >
                        {event.description}
                    </Typography>
                </Collapse>
            )}
        </Box>
    );
}
